import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, Zap, Brain, Shield, TrendingUp, Users, ArrowRight, 
  ShieldAlert, CloudRain, Eye, Sliders, Target, BarChart3,
  DollarSign, AlertTriangle, Play, ChevronRight, Sparkles,
  Store, LineChart, Lock, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProductPage = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const coreCapabilities = [
    {
      icon: ShieldAlert,
      title: "Pricing Guardrails",
      tagline: "Prevent costly mistakes",
      description: "See which price changes are unsafe before you commit. PriceStrike flags risky decisions—like pricing below cost or undercutting your own margins—before they hit your bottom line.",
      benefits: ["Floor/ceiling price alerts", "Margin erosion warnings", "Competitor retaliation risk", "Approval workflows"],
      color: "from-red-500 to-red-600"
    },
    {
      icon: Sliders,
      title: "Real-Time Simulator",
      tagline: "See impact before you act",
      description: "Slide the price up or down and watch the projected impact change in real-time. See trade-offs between volume, revenue, and profit before you make the change.",
      benefits: ["Interactive price slider", "Demand curve visualization", "Margin impact preview", "Volume projections"],
      color: "from-yellow-500 to-yellow-600"
    },
    {
      icon: Brain,
      title: "AI Recommendations",
      tagline: "Smart suggestions, human decisions",
      description: "Get AI-powered pricing recommendations with plain-English explanations. Know exactly why a price is suggested and what happens if you go higher or lower.",
      benefits: ["Explainable AI insights", "Confidence scores", "Alternative scenarios", "Risk assessment"],
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: Eye,
      title: "Market Intelligence",
      tagline: "Know your competitive position",
      description: "Track competitor prices, understand market trends, and identify pricing opportunities. Stay informed without the manual research.",
      benefits: ["Competitor monitoring", "Price gap analysis", "Market trend alerts", "Opportunity identification"],
      color: "from-blue-500 to-blue-600"
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Connect Your Data",
      description: "Import your product catalog and sales history. We work with messy, incomplete data—no PhD required.",
      icon: RefreshCw
    },
    {
      step: "2",
      title: "Set Your Guardrails",
      description: "Define your pricing rules, margin floors, and approval workflows. You stay in control.",
      icon: Shield
    },
    {
      step: "3",
      title: "Get Recommendations",
      description: "Our AI analyzes your data and suggests pricing changes with clear explanations.",
      icon: Sparkles
    },
    {
      step: "4",
      title: "Simulate & Decide",
      description: "Test different scenarios, see projected outcomes, and make confident decisions.",
      icon: Play
    }
  ];

  const smbBenefits = [
    {
      icon: Store,
      title: "Built for Your Size",
      description: "Whether you have 50 SKUs or 5,000, PriceStrike scales to your needs without enterprise complexity or pricing."
    },
    {
      icon: CloudRain,
      title: "Works with Limited Data",
      description: "Most pricing tools need years of clean data. We're designed for the real world—start getting value from day one."
    },
    {
      icon: Lock,
      title: "No Lock-In",
      description: "Month-to-month pricing. Export your data anytime. We earn your business every month."
    },
    {
      icon: Users,
      title: "No Data Scientists Needed",
      description: "Designed for business owners who wear multiple hats. If you can use a spreadsheet, you can use PriceStrike."
    }
  ];

  const integrations = [
    { name: "Shopify", category: "E-commerce", status: "available" },
    { name: "WooCommerce", category: "E-commerce", status: "available" },
    { name: "QuickBooks", category: "Accounting", status: "available" },
    { name: "Xero", category: "Accounting", status: "available" },
    { name: "Square", category: "POS", status: "available" },
    { name: "Lightspeed", category: "POS", status: "available" },
    { name: "Excel/CSV", category: "Import", status: "available" },
    { name: "Google Sheets", category: "Import", status: "available" }
  ];

  const comingSoonIntegrations = [
    { 
      name: "Zapier", 
      category: "Automation",
      description: "Connect PriceStrike to 5,000+ apps. Automate pricing alerts, sync data, and trigger workflows.",
      icon: Zap
    },
    { 
      name: "Jasper AI", 
      category: "Content",
      description: "Auto-generate pricing page copy, product descriptions, and A/B test variants for your listings.",
      icon: Sparkles
    },
    { 
      name: "Surfer SEO", 
      category: "SEO",
      description: "Optimize product pages with pricing-aware SEO suggestions. Improve rankings with competitive insights.",
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827] relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#2563EB] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2563EB] rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#2563EB]" />
              <span className="text-[#2563EB] font-medium text-sm">Pricing Intelligence for SMBs</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-[#F8FAFC] mb-6 leading-tight">
              Stop Guessing.
              <br />
              <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent">
                Start Knowing.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-[#94A3B8] max-w-3xl mx-auto mb-4">
              PriceStrike helps you avoid pricing mistakes before they hurt your business.
            </p>
            <p className="text-lg text-[#F8FAFC] max-w-2xl mx-auto mb-10">
              See the impact of every price change in real-time. No data science degree required.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing-check">
                <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg shadow-xl shadow-[#2563EB]/25">
                  Run Free Pricing Check
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Value Props Row */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: ShieldAlert, text: "Prevent bad pricing moves", color: "text-red-400" },
              { icon: Zap, text: "See impact in real-time", color: "text-yellow-400" },
              { icon: Target, text: "Made for SMB owners", color: "text-green-400" }
            ].map((prop, idx) => (
              <div key={idx} className="flex items-center justify-center gap-3 bg-[#111827]/50 border border-[#2563EB]/20 rounded-xl py-4 px-6">
                <prop.icon className={`w-6 h-6 ${prop.color}`} />
                <span className="text-[#F8FAFC] font-medium">{prop.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Demo Video Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
              See PriceStrike in Action
            </h2>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              Watch how business owners use PriceStrike to make confident pricing decisions.
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-[#2563EB]/30 shadow-2xl shadow-[#2563EB]/20">
            <video 
              controls
              className="w-full aspect-video bg-[#0A0F1C]"
              poster="/demo-poster.jpg"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/t7da38b0_Young%20Man%20.mov" 
                type="video/quicktime" 
              />
              <source 
                src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/t7da38b0_Young%20Man%20.mov" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Core Capabilities - Interactive */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
              Four Ways PriceStrike
              <span className="text-[#2563EB]"> Protects Your Business</span>
            </h2>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
              We don't just find optimal prices—we help you avoid the ones that hurt.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Feature Tabs */}
            <div className="lg:col-span-4 space-y-3">
              {coreCapabilities.map((cap, index) => {
                const Icon = cap.icon;
                return (
                  <button
                    key={index}
                    onClick={() => setActiveFeature(index)}
                    className={`w-full text-left p-5 rounded-xl transition-all ${
                      activeFeature === index
                        ? 'bg-gradient-to-r from-[#2563EB]/20 to-transparent border-l-4 border-[#2563EB]'
                        : 'bg-[#111827]/50 hover:bg-[#111827] border-l-4 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${cap.color} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#F8FAFC]">{cap.title}</h3>
                        <p className="text-sm text-[#94A3B8]">{cap.tagline}</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ml-auto transition-transform ${activeFeature === index ? 'rotate-90 text-[#2563EB]' : 'text-[#94A3B8]'}`} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Feature Detail */}
            <div className="lg:col-span-8">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl p-8 h-full">
                <div className={`w-16 h-16 bg-gradient-to-br ${coreCapabilities[activeFeature].color} rounded-xl flex items-center justify-center mb-6`}>
                  {React.createElement(coreCapabilities[activeFeature].icon, { className: "w-8 h-8 text-white" })}
                </div>
                
                <h3 className="text-3xl font-bold text-[#F8FAFC] mb-2">
                  {coreCapabilities[activeFeature].title}
                </h3>
                <p className="text-[#2563EB] font-medium mb-4">
                  {coreCapabilities[activeFeature].tagline}
                </p>
                
                <p className="text-lg text-[#94A3B8] mb-8 leading-relaxed">
                  {coreCapabilities[activeFeature].description}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {coreCapabilities[activeFeature].benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-[#0A0F1C]/50 rounded-lg p-4">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-[#F8FAFC]">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
              Up and Running in
              <span className="text-[#2563EB]"> Minutes</span>
            </h2>
            <p className="text-xl text-[#94A3B8]">
              No implementation projects. No consultants. No waiting.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Connector Line */}
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-[#2563EB] to-[#2563EB]/30" />
                  )}
                  
                  <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-6 text-center relative z-10">
                    <div className="w-12 h-12 bg-[#2563EB] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                      {step.step}
                    </div>
                    <div className="w-14 h-14 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-[#2563EB]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{step.title}</h3>
                    <p className="text-[#94A3B8] text-sm">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SMB Benefits */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-6 py-2 mb-6">
              <Store className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-medium text-sm">Purpose-Built for SMBs</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
              Finally, Pricing Software That
              <span className="text-[#2563EB]"> Gets It</span>
            </h2>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
              Enterprise tools cost six figures and need a PhD to operate. We built something better.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {smbBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-6 hover:border-[#2563EB]/50 transition-all hover:-translate-y-2">
                  <div className="w-14 h-14 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#2563EB]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{benefit.title}</h3>
                  <p className="text-[#94A3B8]">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section id="integrations" className="py-24 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
            Works with Your
            <span className="text-[#2563EB]"> Existing Tools</span>
          </h2>
          <p className="text-xl text-[#94A3B8] mb-12 max-w-3xl mx-auto">
            Connect in minutes. No data migration headaches. No IT department required.
          </p>
          
          {/* Available Integrations */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-[#111827] border border-[#2563EB]/30 rounded-xl px-6 py-4 hover:border-[#2563EB] transition-all">
                <p className="text-[#F8FAFC] font-semibold">{integration.name}</p>
                <p className="text-xs text-[#94A3B8]">{integration.category}</p>
              </div>
            ))}
          </div>
          
          {/* Coming Soon Integrations */}
          <div className="mt-16 mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-6 py-2 mb-8">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 font-medium text-sm">Coming Soon</span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {comingSoonIntegrations.map((integration, index) => {
                const Icon = integration.icon;
                return (
                  <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-purple-500/30 rounded-2xl p-6 text-left hover:border-purple-500/60 transition-all group">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                        <Icon className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-[#F8FAFC] font-bold text-lg">{integration.name}</p>
                        <p className="text-xs text-purple-400">{integration.category}</p>
                      </div>
                    </div>
                    <p className="text-[#94A3B8] text-sm leading-relaxed">{integration.description}</p>
                    <div className="mt-4 pt-4 border-t border-purple-500/20">
                      <span className="text-xs text-purple-400 font-medium">Notify me when available →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <p className="text-[#94A3B8] mb-8">
            Don't see your tool? We can work with any CSV or Excel export.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#111827] border border-[#2563EB]/40 rounded-3xl p-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-6">
              Ready to Stop Guessing?
            </h2>
            <p className="text-xl text-[#94A3B8] mb-8 max-w-2xl mx-auto">
              See where your pricing is safe. No credit card required. See results in your first week.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/coming-soon">
                <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing-check">
                <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-10 py-7 text-lg font-semibold rounded-lg">
                  Run Free Pricing Check
                </Button>
              </Link>
            </div>
            
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-[#94A3B8]">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                14-day free trial
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                No credit card
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                Cancel anytime
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductPage;
